
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import VideoLinkPreview from '../../components/VideoLinkPreview';
import ImageUrlComponent from '../../components/ImageUrlComponent';
import { OperationRequest } from '@peercolab/engine';
import { Course } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/PathItems';
import { useSpecies } from '../../providers/SpeciesProvider';
import { useCourseCategories } from '../../providers/CourseCategoryProvider';
import { useCourses } from '../../providers/CourseProvider';
import { SearchSpeciesOutput } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/PathItems';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { CourseManagment, SpeciesManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { useTrainers } from '../../providers/TrainerProvider';

interface CourseDetailsTabProps {
  course: Course;
  patchField: <R, V>(operation: OperationRequest<unknown, R>, newValue: V, currentValue: V, revertHandler: (value: V) => void) => Promise<void>;
}

function CourseDetailsTab({ course, patchField }: CourseDetailsTabProps) {
  const { onError} = useToast();
  const { species, loadSpecies } = useSpecies();
  const { courseCategories, loadCourseCategories } = useCourseCategories();
  const { trainers, loadTrainers } = useTrainers();
  const { courses } = useCourses();
  const [type, setType] = useState<'person' | 'pet'>(course.type.type as 'person' | 'pet');
  const [speciesList, setSpeciesList] = useState<SearchSpeciesOutput[]>([]);
  const [courseSpecies, setCourseSpecies] = useState<string | undefined>(course.type.speciesId);
  const [breeds, setBreeds] = useState<string[]>(course.type.breedIds);
  const [courseName, setCourseName] = useState(course.name);
  const [description, setDescription] = useState(course.details.description);
  const [longDescription, setLongDescription] = useState(course.details.longDescription);
  const [categories, setCategories] = useState<string[]>(course.details.categories);
  const [trainer, setTrainer] = useState(course.details.trainerId);
  const [durationTotalMinutes, setDurationTotalMinutes] = useState(course.details.duration);
  const [paymentRef, setPaymentRef] = useState(course.paymentReference.productId);
  const [paymentUrl] = useState('https://payments.example.com');
  const [image, setImage] = useState(course.media.thumbnail.url);
  const [videoLink, setVideoLink] = useState(course.media.video.url);
  const [requiresSequentialCompletion, setRequiresSequentialCompletion] = useState(course.details.sequentialCompletion);

  useEffect(() => {
    const loadData = async () => {
      await loadCourseCategories();
      await loadTrainers();
      onError((await Session.getClient().request(SpeciesManagement.searchSpecies({ query: '', limit: 100, offset: 0 }))
        ).maybeOk(setSpeciesList));
      if (course.type.speciesId)
        await loadSpecies(course.type.speciesId)
    }
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setType(course.type.type as 'person' | 'pet');
    setCourseSpecies(course.type.speciesId);
    setBreeds(course.type.breedIds);
    setCourseName(course.name);
    setDescription(course.details.description);
    setLongDescription(course.details.longDescription);
    setCategories(course.details.categories);
    setTrainer(course.details.trainerId);
    setDurationTotalMinutes(course.details.duration);
    setPaymentRef(course.paymentReference.productId);
    setImage(course.media.thumbnail.url);
    setVideoLink(course.media.video.url);
    setRequiresSequentialCompletion(course.details.sequentialCompletion);
  }, [courses, course])

  const selectedSpecies = species.find(s => s.id === course.type.speciesId);

  const durationDays = Math.floor(durationTotalMinutes / 1440).toString();
  const durationHours = Math.floor((durationTotalMinutes % 1440) / 60).toString();
  const durationMinutes = (durationTotalMinutes % 60).toString();

  const setTotalDurationFromParts = (days: string, hours: string, minutes: string) => {
    const total = (parseInt(days || '0') * 1440) + (parseInt(hours || '0') * 60) + parseInt(minutes || '0');
    setDurationTotalMinutes(total);
  }

  return (
    <Box sx={{ display: 'flex', gap: 4 }}>
      {/* Left column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel id="type-label">Type</InputLabel>
          <Select
            labelId="type-label"
            value={type}
            label="Type"
            onChange={e => {
              patchField(CourseManagment.updateCourseType({ type: e.target.value }), e.target.value, course.type.type, (value) => setType(value as 'person' | 'pet'));
              setType(e.target.value as 'person' | 'pet');
              setBreeds([]);
            }}
          >
            <MenuItem value="person">Person</MenuItem>
            <MenuItem value="pet">Pet</MenuItem>
          </Select>
        </FormControl>
        {type === 'pet' && (
          <FormControl fullWidth>
            <InputLabel id="species-label">Species</InputLabel>
            <Select
              labelId="species-label"
              value={courseSpecies}
              label="Species"
              onChange={e => {
                patchField(CourseManagment.updateCourseSpeciesId({ speciesId: e.target.value ?? undefined }), e.target.value ?? undefined, course.type.speciesId, (value) => setCourseSpecies(value));
                setCourseSpecies(e.target.value ?? undefined);
                setBreeds([]);
              }}
            >
              {speciesList.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {type === 'pet' && selectedSpecies && (
          <FormControl fullWidth>
            <InputLabel id="breeds-label">Breeds</InputLabel>
            <Select
              labelId="breeds-label"
              multiple
              value={breeds}
              onChange={e => {
                patchField(CourseManagment.updateCourseBreedIds({ breedIds: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[] }), e.target.value as string[], course.type.breedIds, (value) => setBreeds(value));
                setBreeds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[]);
              }}
              input={<OutlinedInput label="Breeds" />}
              renderValue={selected => (selected.map(id => selectedSpecies.breeds.find(b => b.breed.id === id)?.breed.name).join(', '))}
            >
              {selectedSpecies.breeds.map(breed => (
                <MenuItem key={breed.breed.id} value={breed.breed.id}>
                  <Checkbox checked={breeds.some(breedId => breed.breed.id === breedId)} />
                  <ListItemText primary={breed.breed.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          label="Course Name"
          value={courseName}
          onChange={e => setCourseName(e.target.value)}
          onBlur={e => patchField(
            CourseManagment.updateCourseName({ name: courseName }),
            courseName,
            course.name,
            setCourseName
          )}
          sx={{ minWidth: 250 }}
        />
        <TextField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          onBlur={e => patchField(
            CourseManagment.updateCourseDescription({ description: description }),
            description,
            course.details.description,
            setDescription
          )}
          multiline
          minRows={2}
        />
        <TextField
          label="Long Description"
          value={longDescription}
          onChange={e => setLongDescription(e.target.value)}
          onBlur={e => patchField(
            CourseManagment.updateCourseLongDescription({ longDescription: longDescription }),
            longDescription,
            course.details.longDescription,
            setLongDescription
          )}
          multiline
          minRows={20}
        />
      </Box>
      {/* Right column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Categories multi-select */}
        <FormControl fullWidth>
          <InputLabel id="categories-label">Categories</InputLabel>
          <Select
            labelId="categories-label"
            multiple
            value={categories}
            onChange={e => {
              const newCategories = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
              patchField(CourseManagment.updateCourseCategories({ categories: newCategories }), newCategories, course.details.categories, setCategories);
              setCategories(newCategories);
            }}
            input={<OutlinedInput label="Categories" />}
            renderValue={selected => (selected.map(id => courseCategories.find(cat => cat.id === id)?.name).join(', '))}
          >
            {courseCategories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>
                <Checkbox checked={categories.indexOf(cat.id) > -1} />
                <ListItemText primary={cat.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Trainer select */}
        <FormControl fullWidth>
          <InputLabel id="trainer-label">Trainer</InputLabel>
          <Select
            labelId="trainer-label"
            value={trainer}
            label="Trainer"
            onChange={e => {
              patchField(CourseManagment.updateCourseTrainerId({ trainerId: e.target.value }), e.target.value, course.details.trainerId, setTrainer);
              setTrainer(e.target.value);
            }}
          >
            {trainers.map(t => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Duration fields */}
        <Box>
          <Box sx={{ mb: 0.5, fontWeight: 500 }}>Course Duration</Box>
          <Box sx={{ mb: 1, color: 'text.secondary', fontSize: 13 }}>
            Specify the total duration for the course. This will be shown to users as the expected time to complete the course.
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Days"
              type="number"
              value={durationDays}
              onChange={e => {
                setTotalDurationFromParts(e.target.value, durationHours, durationMinutes);
                const total = (parseInt(e.target.value || '0') * 1440) + (parseInt(durationHours || '0') * 60) + parseInt(durationMinutes || '0');
                patchField(CourseManagment.updateCourseDuration({ duration: total }), total, course.details.duration, setDurationTotalMinutes);
              }}
              sx={{ width: 80 }}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Hours"
              type="number"
              value={durationHours}
              onChange={e => {
                setTotalDurationFromParts(durationDays, e.target.value, durationMinutes);
                const total = (parseInt(durationDays || '0') * 1440) + (parseInt(e.target.value || '0') * 60) + parseInt(durationMinutes || '0');
                patchField(CourseManagment.updateCourseDuration({ duration: total }), total, course.details.duration, setDurationTotalMinutes);
              }}
              sx={{ width: 80 }}
              inputProps={{ min: 0, max: 23 }}
            />
            <TextField
              label="Minutes"
              type="number"
              value={durationMinutes}
              onChange={e => {
                setTotalDurationFromParts(durationDays, durationHours, e.target.value);
                const total = (parseInt(durationDays || '0') * 1440) + (parseInt(durationHours || '0') * 60) + parseInt(e.target.value || '0');
                patchField(CourseManagment.updateCourseDuration({ duration: total }), total, course.details.duration, setDurationTotalMinutes);
              }}
              sx={{ width: 100 }}
              inputProps={{ min: 0, max: 59 }}
            />
          </Box>
        </Box>
        {/* Requires Sequential Completion checkbox */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <input
            type="checkbox"
            checked={requiresSequentialCompletion}
            onChange={e => {
              patchField(CourseManagment.updateCourseSequentialCompletion({ sequentialCompletion: e.target.checked }), e.target.checked, course.details.sequentialCompletion, setRequiresSequentialCompletion);
              setRequiresSequentialCompletion(e.target.checked);
            }}
            id="sequential-completion"
            style={{ marginRight: 8 }}
          />
          <label htmlFor="sequential-completion" style={{ fontSize: 15, color: '#555' }}>
            Requires Sequential Completion
          </label>
        </Box>
        {/* Payment reference and link */}
        <Box>
          <TextField
            label="Payment Reference"
            value={paymentRef}
            onChange={e => {
              patchField(CourseManagment.updateCoursePaymentProductId({ productId: e.target.value }), e.target.value, course.paymentReference.productId, setPaymentRef);
              setPaymentRef(e.target.value);
            }}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Link href={paymentUrl} target="_blank" rel="noopener">
              Go to Payment System
            </Link>
          </Box>
        </Box>
        {/* Image upload */}
        <ImageUrlComponent value={image} onChange={(img) => {
          patchField(CourseManagment.updateCourseThumbnailUrl({ url: img ?? '' }), img ?? '', course.media.thumbnail.url, setImage);
          setImage(img ?? '');
        }} />
        {/* Video link preview */}
        <VideoLinkPreview value={videoLink} onChange={link => {
          patchField(CourseManagment.updateCourseVideoUrl({ url: link }), link, course.media.video.url, setVideoLink);
          setVideoLink(link);
        }} />
      </Box>
    </Box>
  );
}

export default CourseDetailsTab;
